/**
 * tenant-screens.ts — spis EKRANÓW panelu klienta (nie kartotek), które można wstawić do menu
 * modułu w kreatorze ITE i które pokazuje mapa obszarów (grupa „Funkcje").
 *
 * Jedno źródło dla: palety kreatora menu modułu (ITE), tłumaczenia menu modułu u klienta
 * (czy pozycja jest aktywna) i mapy obszarów. Właściciel = moduł (albo obszar), od którego
 * zależy ekran — u klienta bez niego pozycja jest wyszarzona („Niewykupiony"); `null` = ekran
 * zawsze aktywny.
 *
 * Kartoteki (listy rekordów: Umowy, Towary, Dokumenty KSeF…) NIE należą tutaj — mają formularz
 * i listę do ustawienia, żyją w spisie celów-kartotek. Ustawienia są „elementem systemowym",
 * też nie tutaj.
 *
 * Każda trasa top-level panelu klienta musi być sklasyfikowana: ekran z tego spisu, kartoteka
 * albo jawnie wyłączona (`TENANT_ROUTES_NOT_IN_MENU`) — pilnuje tego test
 * `apps/admin/src/components/tabs/tenant-routes-classified.test.ts`.
 */

/** Od czego zależy ekran u klienta. */
export type TenantScreenOwner =
  | { kind: 'module'; code: string }
  /** Aktywny, gdy klient ma KTÓRYKOLWIEK moduł obszaru (np. pulpit kadrowy — obszar `hr`). */
  | { kind: 'area'; code: string }
  | null;

export interface TenantScreen {
  /** Stały klucz ekranu (część klucza i18n `tenantScreens.<key>`). */
  key: string;
  /** Adres ekranu w panelu klienta (bez parametrów zapytania). */
  path: string;
  owner: TenantScreenOwner;
  /** Ikona lucide (nazwa) — domyślna ikona pozycji menu. */
  iconName: string;
}

export const TENANT_SCREENS = [
  { key: 'dashboard', path: '/dashboard', owner: null, iconName: 'LayoutDashboard' },
  { key: 'calendar', path: '/calendar', owner: null, iconName: 'Calendar' },
  { key: 'area_map', path: '/area-map', owner: null, iconName: 'Map' },
  { key: 'org_chart', path: '/org-chart', owner: null, iconName: 'ListTree' },
  { key: 'hr_dashboard', path: '/hr', owner: { kind: 'area', code: 'hr' }, iconName: 'Users' },
  {
    key: 'schedule',
    path: '/grafik',
    owner: { kind: 'module', code: 'grafiki' },
    iconName: 'Clock',
  },
  {
    key: 'processes',
    path: '/processes',
    owner: { kind: 'module', code: 'processes' },
    iconName: 'GitBranch',
  },
  {
    key: 'decisions_pending',
    path: '/processes/decisions/pending',
    owner: { kind: 'module', code: 'processes' },
    iconName: 'CheckCircle2',
  },
  {
    key: 'decisions_submitted',
    path: '/processes/decisions/submitted',
    owner: { kind: 'module', code: 'processes' },
    iconName: 'Send',
  },
  {
    key: 'decisions_active',
    path: '/processes/decisions/active',
    owner: { kind: 'module', code: 'processes' },
    iconName: 'Activity',
  },
  {
    key: 'process_templates',
    path: '/processes/templates',
    owner: { kind: 'module', code: 'processes' },
    iconName: 'LayoutTemplate',
  },
  {
    key: 'hm_sales',
    path: '/handel-magazyn/sprzedaz',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Receipt',
  },
  {
    key: 'hm_warehouse',
    path: '/handel-magazyn/magazyn',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Warehouse',
  },
  {
    key: 'hm_orders',
    path: '/handel-magazyn/zamowienia',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Clipboard',
  },
  {
    key: 'hm_payments',
    path: '/handel-magazyn/kasa-bank',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Wallet',
  },
  {
    key: 'hm_products',
    path: '/handel-magazyn/towary',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Package',
  },
  {
    key: 'hm_contractors',
    path: '/handel-magazyn/kontrahenci',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Users',
  },
  {
    key: 'hm_connection',
    path: '/handel-magazyn/polaczenie',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Link',
  },
  {
    key: 'hm_fiscal_stations',
    path: '/handel-magazyn/drukarki-fiskalne',
    owner: { kind: 'module', code: 'handel_magazyn' },
    iconName: 'Monitor',
  },
] as const satisfies readonly TenantScreen[];

export type TenantScreenKey = (typeof TENANT_SCREENS)[number]['key'];

/**
 * Trasy top-level panelu klienta, które świadomie NIE są ekranem do menu modułu: konfiguratory,
 * profil, Ustawienia (element systemowy), przekierowania. Kartoteki są klasyfikowane osobno
 * (spis celów-kartotek), strony `new` / `:id` / zestawienia — z definicji poza menu.
 */
export const TENANT_ROUTES_NOT_IN_MENU: readonly string[] = [
  '/',
  '/dashboard/default',
  '/tenants',
  '/settings',
  '/settings/:section',
  '/modules',
  '/system-modules',
  '/menu-builder',
  '/profile',
  '/profile/2fa/setup',
  '/processes/decisions',
  '/processes/decisions/:tab',
];

/** Normalizuje adres pozycji menu: bez query/hash, bez końcowego `/`. */
function normalizePath(url: string): string {
  const [path] = url.split(/[?#]/);
  const trimmed = (path ?? '').replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

const SCREENS_BY_PATH = new Map<string, TenantScreen>(TENANT_SCREENS.map((s) => [s.path, s]));

/** Ekran wskazywany przez adres pozycji menu (`custom_link`) — dokładne dopasowanie ścieżki. */
export function matchTenantScreen(url: string): TenantScreen | null {
  if (!url.startsWith('/')) return null;
  return SCREENS_BY_PATH.get(normalizePath(url)) ?? null;
}

/**
 * Czy ekran jest aktywny u klienta. `activeModuleCodes` — kody modułów z aktywną subskrypcją;
 * `activeAreaCodes` — obszary, w których klient ma co najmniej jeden aktywny moduł.
 */
export function isTenantScreenActive(
  screen: Pick<TenantScreen, 'owner'>,
  activeModuleCodes: ReadonlySet<string>,
  activeAreaCodes: ReadonlySet<string>,
): boolean {
  const { owner } = screen;
  if (!owner) return true;
  return owner.kind === 'module'
    ? activeModuleCodes.has(owner.code)
    : activeAreaCodes.has(owner.code);
}
